import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import LandingPage from './pages/LandingPage';
import FeaturesPage from './pages/FeaturesPage';
import ServiceProvidersPage from './pages/ServiceProvidersPage';
import PricingPage from './pages/PricingPage';

import Login from './pages/Login';
import RegisterSelection from './pages/RegisterSelection';
import OwnerRegistration from './pages/OwnerRegistration';
import ProviderRegistration from './pages/ProviderRegistration';
import RoleBasedDashboard from './components/RoleBasedDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminReports from './pages/AdminReports';
import AdminUsers from './pages/AdminUsers';
import AdminSystem from './pages/AdminSystem';
import TicketList from './pages/TicketList';
import TicketDetail from './pages/TicketDetail';
import PropertiesPage from './pages/PropertiesPage';
import ExpensesPage from './pages/ExpensesPage';
import TenantsPage from './pages/TenantsPage';
import TenantPropertyPage from './pages/TenantPropertyPage';
import TenantDocumentsPage from './pages/TenantDocumentsPage';
import TenantSettingsPage from './pages/TenantSettingsPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import Chat from './pages/Chat';
import JobExplore from './pages/JobExplore';
import EarningsPage from './pages/EarningsPage';
import ProfilePage from './pages/ProfilePage';
import DemoPage from './pages/DemoPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return currentUser ? children : <Navigate to="/login" />;
};

// Public Route Component (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return currentUser ? <Navigate to="/dashboard" /> : children;
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <Router>
          <div className="App">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/providers" element={<ServiceProvidersPage />} />
            <Route path="/pricing" element={<PricingPage />} />

            
                                    <Route
                          path="/login"
                          element={
                            <PublicRoute>
                              <Login />
                            </PublicRoute>
                          }
                        />
                        <Route
                          path="/register"
                          element={
                            <PublicRoute>
                              <RegisterSelection />
                            </PublicRoute>
                          }
                        />
                        <Route
                          path="/register/owner"
                          element={
                            <PublicRoute>
                              <OwnerRegistration />
                            </PublicRoute>
                          }
                        />
                        <Route
                          path="/register/provider"
                          element={
                            <PublicRoute>
                              <ProviderRegistration />
                            </PublicRoute>
                          }
                        />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <RoleBasedDashboard />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/tickets" 
              element={
                <ProtectedRoute>
                  <TicketList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/tickets/:id" 
              element={
                <ProtectedRoute>
                  <TicketDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/properties" 
              element={
                <ProtectedRoute>
                  <PropertiesPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/properties/:propertyId" 
              element={
                <ProtectedRoute>
                  <PropertyDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/expenses" 
              element={
                <ProtectedRoute>
                  <ExpensesPage />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/tenants" 
              element={
                <ProtectedRoute>
                  <TenantsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/property" 
              element={
                <ProtectedRoute>
                  <TenantPropertyPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/documents" 
              element={
                <ProtectedRoute>
                  <TenantDocumentsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <TenantSettingsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/chats" 
              element={
                <ProtectedRoute>
                  <Chat />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/job-explore" 
              element={
                <ProtectedRoute>
                  <JobExplore />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/earnings" 
              element={
                <ProtectedRoute>
                  <EarningsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/reports" 
              element={
                <ProtectedRoute>
                  <AdminReports />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/users" 
              element={
                <ProtectedRoute>
                  <AdminUsers />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/system" 
              element={
                <ProtectedRoute>
                  <AdminSystem />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </div>
        </Router>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
